<div class="flex flex-col self-stretch gap-2.5"
     [formGroup]="contentUnitForm"
>
  <div class="flex justify-between items-center">
    <h1 class="content-unit-headline">Content Unit ({{ manager.isReadOnly() ? 'View' : (manager.isEditMode() ? 'Edit' : 'Create') }} Mode)</h1>

    <div class="flex justify-between items-center mt-4 gap-2.5">
      @if (manager.isViewMode()) {
        <button pButton
                type="button"
                [attr.data-test]="'content-unit-edit-button'"
                [disabled]="!permissionService.hasRedactRights()"
                (click)="onEdit()"
        >
          Edit
        </button>
      }

      @if (manager.isEditMode() || manager.isCreateMode()) {
        <button pButton
                severity="primary"
                type="button"
                [attr.data-test]="'content-unit-save-button'"
                [disabled]="contentUnitForm.invalid"
                (click)="manager.saveContentUnit()"
        >
          Confirm
        </button>
        <button pButton
                severity="secondary"
                type="button"
                [attr.data-test]="'content-unit-cancel-button'"
                [disabled]="!permissionService.hasRedactRights()"
                [outlined]="true"
                (click)="manager.cancelEdit()"
        >
          Cancel
        </button>
      }
      @if (manager.isViewMode()) {
        <button pButton
                severity="secondary"
                type="button"
                [attr.data-test]="'content-unit-close-button'"
                (click)="onClose()"
        >
          Close
        </button>
      }
    </div>
  </div>

  @let titleControl = contentUnitForm.get('title');
  <div>
    <p-iftalabel class="w-full">
      <input class="w-full p-inputtext"
             formControlName="title"
             id="title"
             pInputText
             placeholder="Enter Content Unit title"
             type="text"
             [invalid]="titleControl?.invalid"
             [required]="true"
      />
      <label class="block font-medium mb-1"
             for="title"
      > Title
      </label>
    </p-iftalabel>
    @if (titleControl?.errors?.['required']) {
      <small class="block text-red-500 text-xs mt-1">Title is required.</small>
    }
    @if (titleControl?.errors?.['contentUnitExists'] && titleControl?.touched) {
      <small class="block text-red-500 text-xs mt-1">
        Title exists already, please choose a different name.
      </small>
    }
  </div>

  <div>
    <p-iftalabel class="w-full">
      <textarea
        class="w-full"
        formControlName="description"
        id="description"
        pTextarea
        placeholder="Enter Content Unit description"
        rows="3"
        [autoResize]="false"
      >
      </textarea>
      <label class="block font-medium mb-1"
             for="description"
      > Description
      </label>
    </p-iftalabel>
  </div>
  <div>
    <p-iftalabel>
      <p-autocomplete fluid
                      formControlName="tags"
                      inputId="tags"
                      multiple
                      separator=","
                      [addOnBlur]="true"
                      [addOnTab]="true"
                      [minQueryLength]="3"
                      [suggestions]="manager.suggestedTags()"
                      [typeahead]="true"
                      (completeMethod)="onTagsSearch($event)"
      />
      <label for="tags"> Tags </label>
    </p-iftalabel>
  </div>
  <p-divider align="left"
             type="solid"
  >
    Content
  </p-divider>
  @let contentControl = contentUnitForm.get('content');
  <div [class.code-view]="manager.isCodeView()"
       [class.read-only]="manager.isReadOnly()"
  >
    <p-editor formControlName="content"
              id="content"
              placeholder="Enter content"
              [class]="'ql-editor-content-unit'"
              [invalid]="contentControl?.invalid"
              [readonly]="manager.isReadOnly()"
              [required]="true"
              (onInit)="onEditorInit($event)"
    >
      <ng-template #header>
        <span class="ql-formats">
            <select aria-label="Header"
                    class="ql-header"
                    id="header"
            ></select>
            <select aria-label="Font"
                    class="ql-font"
                    id="font"
            ></select>
        </span>
        <span class="ql-formats">
            <button aria-label="Bold"
                    class="ql-bold"
                    title="Bold"
            ></button>
            <button aria-label="Italic"
                    class="ql-italic"
                    title="Italic"
            ></button>
            <button aria-label="Underline"
                    class="ql-underline"
                    title="Underline"
            ></button>
        </span>
        <span class="ql-formats">
            <button aria-label="Ordered List"
                    class="ql-list"
                    title="Ordered List"
                    value="ordered"
            ></button>
            <button aria-label="Unordered List"
                    class="ql-list"
                    title="Unordered List"
                    value="bullet"
            ></button>
        </span>
        <span class="ql-formats">
          <button aria-label="Remove Styles"
                  class="ql-clean"
                  title="Remove Styles"
          ></button>
        </span>
        <span class="ql-formats">
          <button aria-label="Toggle Code View"
                  class="code-toggle"
                  title="Toggle Code View"
                  type="button"
                  [class.ql-active]="manager.isCodeView()"
                  (click)="manager.toggleCodeView()"
          ><span class="pi pi-code"></span>
          </button>
          </span>
        <span class="ql-formats">
        <p-select
          optionLabel="label"
          optionValue="placeholder"
          placeholder="Placeholders"
          [ariaLabel]="'Placeholder'"
          [disabled]="manager.isReadOnly() || manager.isCodeView()"
          [filter]="true"
          [options]="manager.placeholders()"
          (onChange)="onPlaceholderSelect($event)"
        />
        </span>
      </ng-template>
    </p-editor>
    @if (manager.isCodeView()) {
      <div class="code-editor-container">
        <pre class="code-highlight"><code [innerHTML]="highlightedCode"></code></pre>
        <textarea #codeInput
                  aria-label="Code Input"
                  class="ql-editor-code code-input"
                  formControlName="content"
                  id="codeInput"
                  [readOnly]="manager.isReadOnly()"
                  (scroll)="onCodeScroll($event)"
        ></textarea>
      </div>
    }
  </div>
  @if (contentControl?.errors?.['required']) {
    <small class="block text-red-500 text-xs mt-1">Content is required.</small>
  }
  <div class="flex items-center gap-2"
  >
    <p-toggleswitch formControlName="skipPlaceholder"
                    inputId="skipPlaceholder"
    />
    <label for="skipPlaceholder">Skip placeholders if empty</label>
  </div>

  <p-divider align="left"
             type="solid"
  >
    Contained in
  </p-divider>
  <div class="pl-4.75">
    <ul class="list-disc">
      @for (template of manager.templates(); track template) {
        <li>
          <a class="underline"
             [routerLink]="['/template', template.uuid]"
          >
            {{ template.title }}
          </a>
        </li>
      }
    </ul>
  </div>
</div>
